import { selectCalendarTimeZone } from '@src/packages/utils/dates/calendar';
import React from 'react';

import { Empty } from 'antd';
import { useSelector } from 'react-redux';


import { ResponsiveTable } from '@src/components/Table';
import UserListDrawer from '@src/components/Users/UserListDrawer';

import BoxContainer from '@src/components/BoxContainer';
import Title from '@src/components/BoxContainer/components/Title';
import FilterActiveTags from '@src/components/FilterActiveTags/FilterActiveTags';
import FilterContainer from '@src/components/FilterContainer';
import { FilterValues } from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import ListViewportLayout from '@src/components/ListViewportLayout';
import SecondaryPanel from '@src/components/SecondaryPanel/SecondaryPanel';
import type { ExecutionsHistoryRouteQuery } from '@src/models/interfaces/executionsHistoryDashboard.interface';
import ReportDivisionAccessGate from '@src/packages/dashboard/components/ReportDivisionAccessGate';
import ApplyReportFiltersButton from '@src/packages/dashboard/components/ApplyReportFiltersButton';
import ReportEventDrawer from '@src/packages/dashboard/components/ReportEventDrawer';
import type { ChartView } from '@src/packages/dashboard/components/ReportSummary/types';
import { useServerReportPdf } from '@src/packages/dashboard/hooks/useServerReportPdf';
import { useCostCenterFilterIds } from '@src/packages/costCenters/hooks/useCostCenterFilterIds';
import IntlMessages from '@src/util/IntlMessages';

import ReportTabsBar from '../../components/ReportTabsBar';
import { getDateTypeIndexUTC } from '../../utils';
import { buildScheduleHistoryQuery } from '../SchedulesDashboard/components/ScheduleSummary/scheduleHistoryQuery';
import styles from '../SchedulesDashboard/styles.module.less';
import RouteSummary from './RouteSummary';
import { buildRouteSummaryData } from './RouteSummary/routeSummaryData';
import CheckpointRoutesDashboardFilters from './components/CheckpointRoutesDashboardFilters';
import { useCheckpointRoutesDashboard } from './hooks/useCheckpointRoutesDashboard';
import { useRouteHistorySections } from './hooks/useRouteHistorySections';

const EMPTY_DIVISION_IDS: string[] = [];

export const CheckpointRoutesDashboardContent: React.FC = () => {
  const [activeTab, setActiveTab] = React.useState('summary');
  const viewportBodyRef = React.useRef<HTMLDivElement>(null);
  const filterWheelRegionRef = React.useRef<HTMLDivElement>(null);
  const tabsWheelRegionRef = React.useRef<HTMLDivElement>(null);
  const [complianceChartView, setComplianceChartView] = React.useState<ChartView>('bar');
  const organizationTimeZone = useSelector(selectCalendarTimeZone);

  const {
    mainLoading,
    orgId,
    groupBy,
    openTimelineEvents,
    intl,
    onBackFromTimeline,
    tableDataByDay,
    drawerState,
    toggleDrawer,
    selectedUsers,
    columns,
    filters,
    setFilters,
    initialFilters,
    chartFilters,
    draftChartFilters,
    hasPendingFilters,
    applyFilters,
    fetch,
    queryDivisionIds,
    canQueryDivisions,
    permissionsLoaded,
    disabledDate,
    setSelectedSchedules,
    setScheduleSelectorLoading,
    toggle,
    isOpen,
  } = useCheckpointRoutesDashboard();
  const reportDivisionIds = queryDivisionIds ?? EMPTY_DIVISION_IDS;
  const { queryIds: costCenterQueryIds, isExpandReady: costCenterExpandReady } =
    useCostCenterFilterIds(chartFilters.costCenterIds);
  const routeHistoryQuery = React.useMemo<ExecutionsHistoryRouteQuery | undefined>(() => {
    if (!orgId || !canQueryDivisions || !costCenterExpandReady) return undefined;
    const scheduleQuery = buildScheduleHistoryQuery(
      {
        ...chartFilters,
        costCenterIds: costCenterQueryIds ?? [],
      },
      orgId,
      reportDivisionIds,
      organizationTimeZone,
    );
    const { scheduleIds, ...baseQuery } = scheduleQuery;
    return { ...baseQuery, routeIds: scheduleIds };
  }, [
    canQueryDivisions,
    chartFilters,
    costCenterExpandReady,
    costCenterQueryIds,
    orgId,
    organizationTimeZone,
    reportDivisionIds,
  ]);
  const routeHistory = useRouteHistorySections(routeHistoryQuery);
  const routeSummaryData = React.useMemo(
    () => buildRouteSummaryData(routeHistory.sections),
    [routeHistory.sections],
  );

  const initialFiltersRef = React.useRef(initialFilters);

  const handleStickyControlsWheel = React.useCallback((event: WheelEvent) => {
    if (
      event.ctrlKey ||
      event.shiftKey ||
      event.deltaY === 0 ||
      Math.abs(event.deltaX) > Math.abs(event.deltaY) ||
      !viewportBodyRef.current
    )
      return;

    event.preventDefault();
    viewportBodyRef.current.scrollTop += event.deltaY;
  }, []);

  React.useEffect(() => {
    const regions = [filterWheelRegionRef.current, tabsWheelRegionRef.current].filter(
      (region): region is HTMLDivElement => Boolean(region),
    );
    const options: AddEventListenerOptions = { passive: false };
    regions.forEach(region => region.addEventListener('wheel', handleStickyControlsWheel, options));

    return () =>
      regions.forEach(region =>
        region.removeEventListener('wheel', handleStickyControlsWheel, options),
      );
  }, [handleStickyControlsWheel]);

  const filterPanel = React.useMemo(
    () => (
      <CheckpointRoutesDashboardFilters
        handleFilters={setFilters}
        filters={filters}
        chartFilters={draftChartFilters}
        divisionIds={queryDivisionIds}
        disabledDate={disabledDate}
        onSchedulesChange={setSelectedSchedules}
        onScheduleSelectorLoading={setScheduleSelectorLoading}
      />
    ),
    [
      draftChartFilters,
      disabledDate,
      filters,
      queryDivisionIds,
      setFilters,
      setScheduleSelectorLoading,
      setSelectedSchedules,
    ],
  );

  const serverPdfAction = useServerReportPdf(
    routeHistoryQuery ? { ...routeHistoryQuery, reportType: 'routes', variant: 'full' } : undefined,
  );

  return (
    <>
      <SecondaryPanel.Panel
        title={intl.formatMessage({ id: 'dashboards.chart.filters' })}
        initialValues={initialFiltersRef.current as FilterValues}
      >
        {filterPanel}
        <ApplyReportFiltersButton
          onClick={applyFilters}
          disabled={!hasPendingFilters || mainLoading}
          loading={mainLoading}
        />
      </SecondaryPanel.Panel>
      <BoxContainer className={styles.reportActions} fullContent fixed bottomBorder>
        <FilterContainer
          className="dashboard-report-header"
          title={<Title.Header value={<IntlMessages id="dashboards.checkpoint.routes.title" />} />}
          actionButtons={[
            {
              label: intl.formatMessage({
                id: isOpen ? 'filterDropdown.closeFilters' : 'dashboard.tooltip.filter',
              }),
              iconName: 'filter_alt',
              type: 'secondary',
              action: toggle,
              className: isOpen ? 'gx-border-primary' : '',
            },
            {
              label: intl.formatMessage({ id: 'backoffice.button.refresh' }),
              iconName: 'refresh',
              type: 'secondary',
              action: activeTab === 'summary' ? routeHistory.refresh : fetch,
              disabled: !canQueryDivisions || (activeTab === 'summary' ? routeHistory.loading : mainLoading),
            },
            serverPdfAction,
          ]}
        />
      </BoxContainer>
      <FilterActiveTags.Container ref={filterWheelRegionRef} className={styles.reportFilters}>
        <FilterActiveTags.Bar>
          <FilterActiveTags.ScrollArea>
            <FilterActiveTags onAddFilter={toggle} onCustomFilterChange={setFilters} />
          </FilterActiveTags.ScrollArea>
        </FilterActiveTags.Bar>
      </FilterActiveTags.Container>
      <BoxContainer loading={activeTab === 'detail' && mainLoading} fullContent>
        <SecondaryPanel.Content>
          <ListViewportLayout
            bodyRef={viewportBodyRef}
            className={styles.reportViewport}
            bodyClassName={styles.reportBody}
            viewportOffset={160}
            minHeight="calc(100dvh - 160px)"
            sidePanelOpen={isOpen}
            constrainToScrollParent={false}
            top={
              <ReportTabsBar
                containerRef={tabsWheelRegionRef}
                className={styles.tabsWheelRegion}
                bleed
                activeKey={activeTab}
                onChange={setActiveTab}
                items={[
                  { key: 'summary', label: <IntlMessages id="dashboards.schedules.tab.summary" /> },
                  { key: 'detail', label: <IntlMessages id="dashboards.schedules.tab.detail" /> },
                ]}
              />
            }
            topClassName={styles.tabsHeader}
          >
            <ReportDivisionAccessGate
              canQuery={canQueryDivisions}
              permissionsLoaded={permissionsLoaded}
            >
              {activeTab === 'summary' ? (
                <RouteSummary
                  filters={chartFilters}
                  divisionIds={queryDivisionIds}
                  query={routeHistoryQuery}
                  sections={routeHistory.sections}
                  retrySection={routeHistory.retrySection}
                  data={routeSummaryData}
                  complianceChartView={complianceChartView}
                  onComplianceChartViewChange={setComplianceChartView}
                />
              ) : (
                <div className={styles.progressItemsList}>
                  {!mainLoading && tableDataByDay.length === 0 && (
                    <Empty description={<IntlMessages id="components.empty.description" />} />
                  )}
                  {tableDataByDay.map(dayData => (
                    <div key={dayData.day} className={styles.dayContainer}>
                      <h2 className={styles.day}>{getDateTypeIndexUTC(dayData.day, groupBy, intl)}</h2>
                      <ResponsiveTable
                        className={styles.table}
                        dataSource={dayData.dataSource}
                        columns={columns}
                        loading={false}
                      />
                    </div>
                  ))}
                </div>
              )}
            </ReportDivisionAccessGate>
          </ListViewportLayout>
        </SecondaryPanel.Content>
      </BoxContainer>
      {openTimelineEvents && (
        <ReportEventDrawer
          isVisible={openTimelineEvents}
          onCancel={onBackFromTimeline}
          organizationId={orgId}
          divisionIds={reportDivisionIds}
          scheduleType="checkpoint_route"
        />
      )}
      <UserListDrawer
        isVisible={drawerState.users}
        onCancel={() => toggleDrawer('users')}
        users={selectedUsers?.users || []}
        groups={selectedUsers?.groups || []}
      />
    </>
  );
};

const CheckpointRoutesDashboard: React.FC = () => <CheckpointRoutesDashboardContent />;

export default CheckpointRoutesDashboard;
